// © 2025–2026 Avv. Dr. Gian Marco Solas · All rights reserved
// Corporate Disclosure Scanner · corporate-scanner.com
// Theory of Interrelation · Legal-physics framework
// Published: JLMI 1/25 (DOI 10.13135/2785-7867/12266)
// Pre-print: "Law is Love" — SSRN 5694423 (physics-reviewed)
// Book: De Lege et Amore (ISBN 9791221030631)
// Unauthorised reproduction, adaptation or commercial use is prohibited.
// Share price data via /api/share-price (Vercel proxy to Yahoo Finance)
// λ-divergence = investability score − market valuation percentile

export async function fetchShareData(ticker) {
  if (!ticker) return null;
  try {
    const res = await fetch(`/api/share-price?ticker=${encodeURIComponent(ticker.trim())}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.error || !data.price) return null;
    return data;
  } catch {
    return null;
  }
}

export function calcLambdaDivergence(investabilityScore, shareData) {
  if (!shareData || investabilityScore == null) return null;

  let marketScore = 50;
  if (shareData.peRatio != null && shareData.pbRatio != null) {
    const peNorm = Math.min(100, Math.max(0, (shareData.peRatio / 50) * 100));
    const pbNorm = Math.min(100, Math.max(0, (shareData.pbRatio / 10) * 100));
    marketScore = Math.round((peNorm * 0.6) + (pbNorm * 0.4));
  } else if (shareData.peRatio != null) {
    marketScore = Math.min(100, Math.max(0, Math.round((shareData.peRatio / 50) * 100)));
  } else if (shareData.pbRatio != null) {
    marketScore = Math.min(100, Math.max(0, Math.round((shareData.pbRatio / 10) * 100)));
  }

  const divergence = Math.round(investabilityScore - marketScore);

  return {
    divergence,
    marketScore,
    signal: divergence > 20  ? 'UNDERVALUED'
          : divergence < -20 ? 'OVERVALUED'
          : 'FAIRLY VALUED',
    signalColor: divergence > 20  ? '#34d399'
               : divergence < -20 ? '#f87171'
               : '#fbbf24',
    description: divergence > 20
      ? `Scanner investability (${investabilityScore}) exceeds market valuation score (${marketScore}) — governance quality not fully priced in`
      : divergence < -20
      ? `Market valuation score (${marketScore}) exceeds scanner investability (${investabilityScore}) — market overpricing governance-impaired entity`
      : `Scanner and market broadly aligned (divergence: ${divergence > 0 ? '+' : ''}${divergence})`,
  };
}

export function fmtCap(n) {
  if (!n) return '—';
  if (n >= 1e12) return `$${(n/1e12).toFixed(2)}T`;
  if (n >= 1e9)  return `$${(n/1e9).toFixed(2)}B`;
  if (n >= 1e6)  return `$${(n/1e6).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

export function fmtPrice(n, currency) {
  if (!n) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'GBp' ? 'p' : currency === 'EUR' ? '€' : '$';
  return `${sym}${n.toFixed(2)}`;
}
